import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Calendar, TrendingUp, Users } from "lucide-react";
import { useClientFollowUps, useFollowUpStats } from "@/hooks/useClientFollowUps";
import { useEmployee } from "@/hooks/useEmployee";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

export default function ClientFollowUpManagement() {
  const { data: currentEmployee } = useEmployee();
  const { data: followUps, isLoading } = useClientFollowUps(currentEmployee?.id);
  const { data: stats } = useFollowUpStats(currentEmployee?.id);
  const [activeTab, setActiveTab] = useState("all");

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: "bg-yellow-500",
      completed: "bg-green-500",
      cancelled: "bg-red-500",
    };
    return colors[status] || "bg-gray-500";
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      high: "bg-red-500",
      medium: "bg-yellow-500",
      low: "bg-blue-500",
    };
    return colors[priority] || "bg-gray-500";
  };

  const filteredFollowUps = followUps?.filter((f) => {
    if (activeTab === "all") return true;
    return f.status === activeTab;
  });

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">إدارة متابعة العملاء</h1>
          <p className="text-muted-foreground">تتبع وإدارة جميع المتابعات مع العملاء</p>
        </div>
        <Button>
          <Plus className="ml-2 h-4 w-4" />
          متابعة جديدة
        </Button>
      </div>

      {/* إحصائيات */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">إجمالي المتابعات</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(stats as any)?.total_followups || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">قيد الانتظار</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(stats as any)?.pending || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">مكتملة</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(stats as any)?.completed || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">معدل التحويل</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(stats as any)?.conversion_rate || 0}%</div>
          </CardContent>
        </Card>
      </div>

      {/* قائمة المتابعات */}
      <Card>
        <CardHeader>
          <CardTitle>المتابعات</CardTitle>
          <CardDescription>جميع المتابعات المجدولة والمكتملة</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">الكل</TabsTrigger>
              <TabsTrigger value="pending">قيد الانتظار</TabsTrigger>
              <TabsTrigger value="completed">مكتملة</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="space-y-4 mt-4">
              {isLoading ? (
                <div className="text-center py-8">جاري التحميل...</div>
              ) : filteredFollowUps?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">لا توجد متابعات</div>
              ) : (
                filteredFollowUps?.map((followUp) => (
                  <Card key={followUp.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{followUp.clients?.name_ar}</h3>
                            <Badge className={getStatusColor(followUp.status)}>
                              {followUp.status === "pending" ? "قيد الانتظار" : "مكتملة"}
                            </Badge>
                            <Badge className={getPriorityColor(followUp.priority)}>
                              {followUp.priority === "high" ? "عالية" : followUp.priority === "medium" ? "متوسطة" : "منخفضة"}
                            </Badge>
                          </div>

                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>نوع المتابعة: {followUp.follow_up_type}</p>
                            {followUp.scheduled_date && (
                              <p>موعد المتابعة: {format(new Date(followUp.scheduled_date), "PPP", { locale: ar })}</p>
                            )}
                            {followUp.clients?.mobile && <p>رقم الهاتف: {followUp.clients.mobile}</p>}
                            {followUp.notes && <p className="mt-2">الملاحظات: {followUp.notes}</p>}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">عرض</Button>
                          <Button variant="outline" size="sm">تعديل</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}